import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import auth from './Auth';
import { setUnauthorizedCallback } from './Api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  const handleLogout = useCallback(() => {
    auth.clearAuth();
    setIsAuthenticated(false);
    setUser(null);
  }, []);

  const checkAuthStatus = useCallback(() => {
    const authenticated = auth.isAuthenticated();
    setIsAuthenticated(authenticated);

    if (authenticated) {
      setUser(auth.getUser());
    } else {
      handleLogout();
    }
  }, [handleLogout]);

  // Initial auth check
  useEffect(() => {
    checkAuthStatus();
    setLoading(false);
  }, [checkAuthStatus]);

  // Proactive token expiry check every 30 seconds
  // Catches expiry while the user is idle (no requests being made)
  useEffect(() => {
    const interval = setInterval(() => {
      if (isAuthenticated && !auth.isAuthenticated()) {
        handleLogout();
      }
    }, 30_000);

    return () => clearInterval(interval);
  }, [isAuthenticated, handleLogout]);

  // Listen for storage changes (login/logout in another tab)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'rental_management_token' || e.key === 'rental_management_user') {
        checkAuthStatus();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [checkAuthStatus]);

  const login = async (credentials) => {
    const response = await import('./Api.js').then(m => m.default.auth.login(credentials));
    setIsAuthenticated(true);
    setUser(auth.getUser());
    return response;
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    logout: handleLogout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Wrapper that wires up the 401 redirect + sets up navigation
export const AuthProviderWithRouter = ({ children }) => {
  const navigate = useNavigate();

  useEffect(() => {
    setUnauthorizedCallback(() => {
      auth.clearAuth();
      navigate('/login', {
        replace: true,
        state: { message: 'Your session has expired. Please sign in again.' }
      });
    });
  }, [navigate]);

  return <AuthProvider>{children}</AuthProvider>;
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;